"use client"
import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs"
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar"
import { ThumbsUp, ThumbsDown, MessageSquare, Edit, DollarSign } from "lucide-react"
import { Textarea } from "../../ui/textarea"
import useBudget from "../../../hooks/useBudget"

const BudgetModule = ({ actionId, action }) => {
  console.log("BudgetModule rendered with actionId:", actionId)

  const {
    budget,
    suggestions,
    comments,
    newSuggestion,
    newComment,
    activeTab,
    loading,
    setActiveTab,
    setNewComment,
    updateNewSuggestion,
    handleAddBreakdownItem,
    handleRemoveBreakdownItem,
    handleBreakdownItemChange,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote,
    formatCurrency,
  } = useBudget(actionId, action)

  console.log("BudgetModule hook data:", {
    budgetAmount: budget.amount,
    suggestionsCount: suggestions.length,
    commentsCount: comments.length,
    loading,
  })

  if (loading) {
    return <div className="p-4">Loading budget data...</div>
  }

  return (
    <div>
      <CardHeader className="px-0 pt-0">
        <CardTitle>Estimated Budget</CardTitle>
      </CardHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="current">Current</TabsTrigger>
          <TabsTrigger value="suggestions">Suggestions ({suggestions.length})</TabsTrigger>
          <TabsTrigger value="discussion">Discussion ({comments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="current">
          <Card>
            <CardContent className="p-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Total Budget</h3>
                  <div className="text-2xl font-bold flex items-center">
                    <DollarSign className="h-5 w-5 mr-1" />
                    {formatCurrency(budget.amount)}
                  </div>
                </div>

                {budget.justification && (
                  <div>
                    <h4 className="font-medium mb-1">Justification</h4>
                    <p className="text-sm">{budget.justification}</p>
                  </div>
                )}

                {budget.breakdown && budget.breakdown.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Budget Breakdown</h4>
                    <div className="border rounded-md overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Item
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Cost
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {budget.breakdown.map((item, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm">{item.item}</td>
                              <td className="px-4 py-2 text-sm text-right">{formatCurrency(item.cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <Button onClick={() => setActiveTab("suggestions")} variant="outline">
                    <Edit className="mr-2 h-4 w-4" /> Suggest Edit
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suggestions">
          <Card className="mb-4">
            <CardContent className="p-4">
              <h3 className="text-lg font-medium mb-4">Suggest a Budget Update</h3>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="budget-amount">Total Budget Amount</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                      $
                    </span>
                    <Input
                      id="budget-amount"
                      type="number"
                      value={newSuggestion.amount}
                      onChange={(e) => updateNewSuggestion("amount", e.target.value)}
                      placeholder="Enter budget amount"
                      className="rounded-l-none"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget-justification">Justification</Label>
                  <Textarea
                    id="budget-justification"
                    value={newSuggestion.justification}
                    onChange={(e) => updateNewSuggestion("justification", e.target.value)}
                    placeholder="Explain why this budget is appropriate..."
                    className="min-h-[100px]"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label>Budget Breakdown</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddBreakdownItem}>
                      Add Item
                    </Button>
                  </div>

                  {newSuggestion.breakdown.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <div className="flex-1">
                        <Input
                          placeholder="Item description"
                          value={item.item}
                          onChange={(e) => handleBreakdownItemChange(index, "item", e.target.value)}
                        />
                      </div>
                      <div className="w-1/3">
                        <div className="flex">
                          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                            $
                          </span>
                          <Input
                            type="number"
                            placeholder="Cost"
                            value={item.cost}
                            onChange={(e) => handleBreakdownItemChange(index, "cost", e.target.value)}
                            className="rounded-l-none"
                          />
                        </div>
                      </div>
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveBreakdownItem(index)}
                          className="text-red-500"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSubmitSuggestion}>Submit Budget Suggestion</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {suggestions.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No budget suggestions yet. Be the first to suggest a budget!
            </p>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion) => (
                <Card key={suggestion.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar>
                        <AvatarImage src={suggestion.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{suggestion.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{suggestion.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(suggestion.createdAt).toLocaleString()}
                          </span>
                        </div>

                        <div className="space-y-3 mb-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Suggested Budget:</span>
                            <span className="font-bold">{formatCurrency(suggestion.amount)}</span>
                          </div>

                          <div>
                            <span className="text-sm font-medium">Justification:</span>
                            <p className="text-sm mt-1">{suggestion.justification}</p>
                          </div>

                          {suggestion.breakdown && suggestion.breakdown.length > 0 && (
                            <div>
                              <span className="text-sm font-medium">Breakdown:</span>
                              <div className="border rounded-md overflow-hidden mt-1">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Item
                                      </th>
                                      <th className="px-3 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Cost
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {suggestion.breakdown.map((item, index) => (
                                      <tr key={index}>
                                        <td className="px-3 py-1 text-xs">{item.item}</td>
                                        <td className="px-3 py-1 text-xs text-right">{formatCurrency(item.cost)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "up")}
                            className={suggestion.userVote === "up" ? "bg-green-50" : ""}
                          >
                            <ThumbsUp className="h-4 w-4 mr-1" />
                            <span>{suggestion.upvotes || 0}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "down")}
                            className={suggestion.userVote === "down" ? "bg-red-50" : ""}
                          >
                            <ThumbsDown className="h-4 w-4 mr-1" />
                            <span>{suggestion.downvotes || 0}</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="discussion">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
                {comments.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No comments yet. Start the discussion!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3 p-3 rounded-lg bg-gray-50">
                      <Avatar>
                        <AvatarImage src={comment.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{comment.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{comment.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-2">
                <Textarea
                  placeholder="Add to the discussion..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex justify-end">
                  <Button onClick={handleSubmitComment}>
                    <MessageSquare className="mr-2 h-4 w-4" /> Post Comment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default BudgetModule
